import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import FormRow from 'components/App/DesktopView/Layout/RightSidebar/InAppCards/shared/LinkResource/FormRow'
import ModalContent from 'components/App/DesktopView/Layout/RightSidebar/InAppCards/shared/LinkResource/ModalContent'
import Sidebar from 'shared/components/ui/Sidebar'
import ValidatedField from 'shared/components/ui/ValidatedField'
import { Button, Label, Notification, Tooltip } from 'shared/ui'
import { Dropdown } from 'util/ui'

import {
  doLinkLinearIssueToTicket,
  doSearchLinearIssues,
} from 'ducks/integrations/linear'
import ProjectLabel from 'components/App/DesktopView/Layout/RightSidebar/InAppCards/shared/ProjectLabel'
import { claimPending } from '../pending'

const LINK_FAILED_HEADER = 'Link failed'
const SEARCH_PLACEHOLDER = 'Search by issue key or title'
const MIN_SEARCH_CHARS = 2
const SEARCH_DEBOUNCE_MS = 300

function dontFilter(results) {
  return results
}

function selectIssueLabels(issue) {
  if (Array.isArray(issue.labels)) return issue.labels
  return issue.labels?.nodes || []
}

export function mapLinearIssueSearchResult(issue) {
  const metadata = [
    issue.team?.name,
    issue.state?.name || issue.state?.type,
    issue.project?.name,
    issue.assignee?.displayName || issue.assignee?.name,
    selectIssueLabels(issue)
      .map(label => label.name)
      .filter(Boolean)
      .join(', '),
  ]
    .filter(Boolean)
    .join(' · ')

  return {
    key: issue.id,
    value: issue.id,
    text: `${issue.identifier} ${issue.title}`,
    children: (
      <React.Fragment>
        <div>
          <strong>{issue.identifier}</strong> {issue.title}
        </div>
        {!!metadata && (
          <div>
            <ProjectLabel>{metadata}</ProjectLabel>
          </div>
        )}
      </React.Fragment>
    ),
  }
}

export default function LinkExistingLinearIssueModal() {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [issueReference, setIssueReference] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [pending, setPending] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const pendingRef = useRef(false)
  const searchTimerRef = useRef(null)
  const searchRequestRef = useRef(0)

  const clearSearchTimer = useCallback(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
      searchTimerRef.current = null
    }
  }, [])

  const resetSearchState = useCallback(
    () => {
      clearSearchTimer()
      searchRequestRef.current += 1
      setIssueReference('')
      setSearchQuery('')
      setSearchResults([])
      setSearchLoading(false)
      setSearchError(null)
    },
    [clearSearchTimer]
  )

  const close = useCallback(
    () => {
      setOpen(false)
      setFeedback(null)
      resetSearchState()
    },
    [resetSearchState]
  )

  const handleDismiss = useCallback(
    () => {
      if (pendingRef.current) return
      close()
    },
    [close]
  )

  const show = useCallback(() => setOpen(true), [])

  const canSubmit = (issueReference || searchQuery).trim().length > 0

  const handleIssueReferenceChange = useCallback((_, { value }) => {
    setIssueReference(value)
  }, [])

  const handleSearchChange = useCallback(
    (_, { searchQuery: nextSearchQuery }) => {
      const trimmed = nextSearchQuery.trim()
      setSearchQuery(nextSearchQuery)
      setIssueReference('')
      setSearchError(null)
      setSearchResults([])
      clearSearchTimer()

      if (trimmed.length < MIN_SEARCH_CHARS) {
        setSearchResults([])
        setSearchLoading(false)
        return
      }

      const requestId = ++searchRequestRef.current
      searchTimerRef.current = setTimeout(() => {
        setSearchLoading(true)
        dispatch(doSearchLinearIssues(trimmed))
          .then(results => {
            if (searchRequestRef.current !== requestId) return
            setSearchResults((results || []).map(mapLinearIssueSearchResult))
            setSearchLoading(false)
          })
          .catch(error => {
            if (searchRequestRef.current !== requestId) return
            setSearchResults([])
            setSearchError(error)
            setSearchLoading(false)
          })
      }, SEARCH_DEBOUNCE_MS)
    },
    [clearSearchTimer, dispatch]
  )

  useEffect(
    () => () => {
      clearSearchTimer()
      searchRequestRef.current += 1
    },
    [clearSearchTimer]
  )

  const handleLink = useCallback(
    () => {
      if (!claimPending(pendingRef)) return
      setFeedback(null)
      setPending(true)
      const reference = issueReference || searchQuery
      dispatch(doLinkLinearIssueToTicket(reference.trim()))
        .then(() => {
          close()
        })
        .catch(() => {
          setFeedback({
            header: LINK_FAILED_HEADER,
            message: 'Could not link existing Linear issue. Try again.',
            type: 'negative',
          })
        })
        .finally(() => {
          pendingRef.current = false
          setPending(false)
        })
    },
    [close, dispatch, issueReference, searchQuery]
  )

  const noResultsMessage = useMemo(
    () => {
      if (searchError) return 'Search failed'
      if (searchLoading) return 'Searching...'
      if (searchQuery.trim().length < MIN_SEARCH_CHARS)
        return SEARCH_PLACEHOLDER
      if (searchResults.length === 0) return 'No issues found'
      return null
    },
    [searchError, searchLoading, searchQuery, searchResults.length]
  )

  const footerChildren = useMemo(
    () => [
      <Tooltip
        key="link"
        portal
        position="top"
        tooltip={!canSubmit && SEARCH_PLACEHOLDER}
      >
        <Button
          disabled={!canSubmit || pending}
          fluid
          onClick={handleLink}
          primary
        >
          Link issue
        </Button>
      </Tooltip>,
      <Button
        basic
        fluid
        key="cancel"
        className="grui mt-4"
        disabled={pending}
        onClick={handleDismiss}
      >
        Cancel
      </Button>,
    ],
    [canSubmit, handleDismiss, handleLink, pending]
  )

  return (
    <React.Fragment>
      <Button basic className="grui mt-4" onClick={show}>
        Existing
      </Button>
      <Sidebar
        footerChildren={footerChildren}
        headerChildren="Link to Existing Linear Issue"
        onClose={handleDismiss}
        open={open}
      >
        <ModalContent>
          {feedback && (
            <Notification
              fluid
              header={feedback.header}
              message={feedback.message}
              type={feedback.type}
            />
          )}
          <FormRow>
            <Label as="div">Issue</Label>
            <ValidatedField
              fluid
              loading={searchLoading}
              name="issue"
              noResultsMessage={noResultsMessage}
              options={searchResults}
              onChange={handleIssueReferenceChange}
              onSearchChange={handleSearchChange}
              placeholder={SEARCH_PLACEHOLDER}
              search={dontFilter}
              selection
              selectOnNavigation={false}
              searchQuery={searchQuery}
              validatedFieldComponent={Dropdown}
              value={issueReference}
            />
          </FormRow>
        </ModalContent>
      </Sidebar>
    </React.Fragment>
  )
}
